import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Table, Thead, Tbody, Tr, Th, Td, Button,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
  useDisclosure, Flex, Text, Box, HStack, Input, Tooltip, IconButton,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, useToast,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { FiEdit2, FiTrash2, FiDownload } from 'react-icons/fi';
import axiosInstance from '../../../api/AxiosInstance';
import FormularioPromotor from './FormularioPromotor';
import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';

const TablaPromotoresCulturales: React.FC = () => {
  const toast = useToast();
  const cancelRef = useRef<any>(null);

  const { isOpen: isOpenNuevo, onOpen: onOpenNuevo, onClose: onCloseNuevo } = useDisclosure();
  const { isOpen: isOpenEditar, onOpen: onOpenEditar, onClose: onCloseEditar } = useDisclosure();
  const { isOpen: isOpenEliminar, onOpen: onOpenEliminar, onClose: onCloseEliminar } = useDisclosure();

  const [data, setData] = useState<any[]>([]);
  const [busqueda, setBusqueda] = useState('');
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [eliminando, setEliminando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const { data: res } = await axiosInstance.get('/cultura/promotores');
      setData(res);
    } catch {
      toast({ title: 'Error al cargar promotores', status: 'error', duration: 3000 });
    }
  }, [toast]);

  useEffect(() => { cargar(); }, [cargar]);

  const cargarDetalle = async (p: any) => {
    try {
      const { data: res } = await axiosInstance.get(`/cultura/promotores/${p.id}`);
      return res;
    } catch {
      return p;
    }
  };

  const filtrados = data.filter((p) =>
    !busqueda ||
    p.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
    (p.especialidad || '').toLowerCase().includes(busqueda.toLowerCase()) ||
    (p.giro || '').toLowerCase().includes(busqueda.toLowerCase())
  );

  const handleEliminar = async () => {
    if (!selectedItem) return;
    setEliminando(true);
    try {
      await axiosInstance.delete(`/cultura/promotores/${selectedItem.id}`);
      toast({ title: 'Promotor eliminado', status: 'success', duration: 3000 });
      onCloseEliminar();
      cargar();
    } catch (e: any) {
      toast({ title: 'Error al eliminar', description: e?.response?.data?.message, status: 'error', duration: 3000 });
    } finally {
      setEliminando(false);
    }
  };

  const descargarFicha = (p: any) => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text('Ficha de Promotor Cultural', 14, 18);
    const datos: [string, string][] = [
      ['Nombre', p.nombre || ''],
      ['CURP', p.curp || ''],
      ['RFC', p.rfc || ''],
      ['Dirección', p.direccion || ''],
      ['Teléfono', p.telefono || ''],
      ['Correo', p.correo || ''],
      ['Giro cultural', p.giro || ''],
      ['Temas', p.temas || ''],
      ['Estudios', p.estudios || ''],
      ['Especialidad', p.especialidad || ''],
      ['Fecha de registro', p.fechaRegistro ? new Date(p.fechaRegistro).toLocaleDateString('es-MX') : ''],
    ];
    autoTable(doc, {
      startY: 26,
      head: [['Campo', 'Valor']],
      body: datos,
      styles: { fontSize: 10 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50 } },
    });
    if (p.trayectoria) {
      const finalY = (doc as any).lastAutoTable.finalY + 8;
      doc.setFontSize(11);
      doc.text('Trayectoria:', 14, finalY);
      doc.setFontSize(9);
      const lines = doc.splitTextToSize(p.trayectoria, 180);
      doc.text(lines, 14, finalY + 6);
    }
    doc.save(`Ficha_${p.nombre}.pdf`);
  };

  const formatFecha = (f: string) => f ? new Date(f).toLocaleDateString('es-MX') : '-';

  return (
    <Box bg="white" borderRadius="lg" boxShadow="sm" p={4}>
      <Flex mb={4} gap={3} flexWrap="wrap" align="center" justify="space-between">
        <Input
          placeholder="Buscar por nombre, especialidad o giro..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          size="sm"
          w={{ base: 'full', md: '280px' }}
        />
        <Button size="sm" leftIcon={<AddIcon />} bg="#5b182b" color="white" _hover={{ bg: '#691f30' }}
          onClick={onOpenNuevo}>
          Nuevo promotor
        </Button>
      </Flex>

      <Box overflowX="auto">
        <Table size="sm" variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th>Nombre</Th>
              <Th>CURP</Th>
              <Th>Giro</Th>
              <Th>Especialidad</Th>
              <Th>Teléfono</Th>
              <Th>Registro</Th>
              <Th>Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filtrados.length === 0 ? (
              <Tr><Td colSpan={7} textAlign="center" color="gray.400" py={6}>Sin promotores registrados</Td></Tr>
            ) : filtrados.map((p) => (
              <Tr key={p.id} _hover={{ bg: 'gray.50' }}>
                <Td fontWeight="medium">{p.nombre}</Td>
                <Td fontSize="xs">{p.curp || '-'}</Td>
                <Td>{p.giro || '-'}</Td>
                <Td>{p.especialidad || '-'}</Td>
                <Td>{p.telefono || '-'}</Td>
                <Td>{formatFecha(p.fechaRegistro)}</Td>
                <Td>
                  <HStack spacing={1}>
                    <Tooltip label="Descargar ficha">
                      <IconButton aria-label="Ficha" icon={<FiDownload />} size="xs" variant="ghost" colorScheme="blue"
                        onClick={() => descargarFicha(p)} />
                    </Tooltip>
                    <Tooltip label="Editar">
                      <IconButton aria-label="Editar" icon={<FiEdit2 />} size="xs" variant="ghost"
                        onClick={async () => { const det = await cargarDetalle(p); setSelectedItem(det); onOpenEditar(); }} />
                    </Tooltip>
                    <Tooltip label="Eliminar">
                      <IconButton aria-label="Eliminar" icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red"
                        onClick={() => { setSelectedItem(p); onOpenEliminar(); }} />
                    </Tooltip>
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* Modal Nuevo */}
      <Modal isOpen={isOpenNuevo} onClose={onCloseNuevo} size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">Nuevo Promotor</ModalHeader>
          <ModalBody py={4}>
            <FormularioPromotor onGuardado={() => { onCloseNuevo(); cargar(); }} />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Modal Editar */}
      <Modal isOpen={isOpenEditar} onClose={onCloseEditar} size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">Editar Promotor</ModalHeader>
          <ModalBody py={4}>
            <FormularioPromotor promotor={selectedItem} onGuardado={() => { onCloseEditar(); cargar(); }} />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Confirmar eliminar */}
      <AlertDialog isOpen={isOpenEliminar} leastDestructiveRef={cancelRef} onClose={onCloseEliminar}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader color="#5b182b">Eliminar promotor</AlertDialogHeader>
            <AlertDialogBody>
              ¿Eliminar a <strong>{selectedItem?.nombre}</strong>? Se eliminarán todos sus archivos.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onCloseEliminar} isDisabled={eliminando}>Cancelar</Button>
              <Button colorScheme="red" onClick={handleEliminar} ml={3} isLoading={eliminando} loadingText="Eliminando...">
                Eliminar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default TablaPromotoresCulturales;
